import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useToast } from "@/components/ui/toast";
import { api, ApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { StripePaymentElement } from "@/components/StripePaymentElement";
import BrandLogo from "@/components/BrandLogo";
import { TierSelector, PENDING_STRIPE_PAYMENT_KEY, readPendingStripePayment } from "@/components/TierSelector";
import { planFeatures, tierColumnIndex } from "@/lib/tierFeatures";
import {
  Check, RefreshCw, ShieldCheck, Lock, CreditCard,
  Loader2, CalendarDays, Zap, Sparkles, Info, X,
  ArrowUpCircle, ArrowDownCircle, AlertTriangle, MessageSquare,
  Ticket, Clock, CalendarCheck,
} from "lucide-react";

// Quick-pick reasons offered when a buyer opens the cancellation dialog.
const CANCEL_REASONS = [
  "Too expensive",
  "Not finding the right listings",
  "Found a deal already",
  "Missing features I need",
  "Just exploring for now",
  "Other",
] as const;

interface CurrentTier {
  tier_id: number;
  tier_name: string;
  amount_per_month: number;
  amount_per_year: number | null;
  interval: string | null;
  tier_is_selectable: boolean;
  features_text: string | null;
  started_at: string | null;
  next_billing_date: string | null;
  can_downgrade: boolean;
  stripe_managed: boolean;
  stripe_subscription_id: string | null;
  card_brand: string | null;
  card_last4: string | null;
  cancel_at_period_end: boolean;
  cancels_at: string | null;
  subscription_status: string | null;
  payment_failed: boolean;
  is_trialing: boolean;
  trial_end: string | null;
  upcoming_charge_amount: number | null;
  // Most recent promo code the buyer redeemed. promo_is_current marks the one
  // actually granting the access they're looking at (vs. a past redemption).
  promo_code: string | null;
  promo_redeemed_at: string | null;
  promo_access_days: number | null;
  promo_expires_at: string | null;
  promo_is_current: boolean;
}

interface AvailableTier {
  id: number;
  name: string;
  amount_per_month: number;
  amount_per_year: number | null;
  features_text: string | null;
  free_listings_access: number | null;
  listing_locks_allowed: number | null;
  advance_access_hours: number | null;
  lock_duration_hours: number | null;
}

/** Payment-modal chrome matching TierSelector's choose-plan modal: brand header,
    amber billing banner, left order summary, right payment column. Payment logic
    stays with the caller (StripePaymentElement children). */
function BrandedPaymentModal({
  onClose,
  closable,
  banner,
  planName,
  price,
  priceNote,
  badges,
  rows,
  alert,
  children,
}: {
  onClose: () => void;
  closable: boolean;
  banner: React.ReactNode;
  planName: string;
  price: string;
  priceNote: string;
  badges?: string[];
  rows: [string, string][];
  alert?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6"
      style={{ backgroundColor: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)" }}
      onClick={(e) => { if (e.target === e.currentTarget && closable) onClose(); }}
    >
      <div
        className="relative flex w-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl animate-in fade-in zoom-in-95 duration-200"
        style={{ maxHeight: "calc(100vh / var(--app-zoom) - 3rem)" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 sm:px-7 pt-4 pb-3.5 border-b border-border shrink-0">
          <BrandLogo className="h-7 w-auto object-contain" />
          {closable && (
            <button
              onClick={onClose}
              className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-border hover:bg-slate-100 text-muted-foreground transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto">
          {/* Billing banner */}
          <div className="border-b border-border px-4 sm:px-6 py-3">
            <div className="flex items-start gap-2.5 rounded-xl bg-amber-50 border border-amber-200 px-3.5 py-3 text-xs text-amber-800">
              <Info className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
              <p>{banner}</p>
            </div>
          </div>

          <div className="flex flex-col md:flex-row">
            {/* Left: order summary */}
            <div className="md:w-[44%] shrink-0 bg-slate-50/70 border-b md:border-b-0 md:border-r border-border px-4 sm:px-6 py-4 sm:py-5 flex flex-col gap-3 sm:gap-4">
              {alert}
              <div className="rounded-xl border border-border bg-white p-4 shadow-sm">
                <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1">Selected plan</p>
                <div className="flex items-start justify-between">
                  <p className="text-xl font-extrabold text-foreground leading-tight">{planName}</p>
                  <div className="text-right shrink-0 ml-2">
                    <p className="text-2xl font-extrabold text-foreground leading-none">{price}</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">{priceNote}</p>
                  </div>
                </div>
                {badges && badges.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {badges.map((b) => (
                      <span key={b} className="inline-flex items-center gap-1 rounded-full border border-border bg-slate-50 px-2.5 py-1 text-[10px] font-semibold text-muted-foreground">
                        <ShieldCheck className="h-3 w-3 text-emerald-500" />
                        {b}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Order rows — last row rendered bold as the amount due */}
              <div className="rounded-xl border border-border bg-white shadow-sm divide-y divide-border">
                {rows.map(([label, value], i) => (
                  <div
                    key={label}
                    className={`flex items-center justify-between px-4 py-2.5 text-sm ${i === rows.length - 1 ? "font-extrabold text-foreground" : "text-muted-foreground"}`}
                  >
                    <span>{label}</span>
                    <span className={i === rows.length - 1 ? "" : "font-semibold text-foreground"}>{value}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: payment form */}
            <div className="flex-1 px-4 sm:px-6 py-4 sm:py-5 flex flex-col gap-3">
              <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Payment details</p>
              {children}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Indexed by the plan's column in the shared feature matrix (0 = Explorer,
// 1 = Operator) — NOT by tier id. Ids are environment-specific and the retired
// "Tier 1-4" rows hold the low ids, so keying on them silently gave Operator
// Explorer's icon and dropped its "Most popular" badge.
const TIER_META: { icon: React.ReactNode; iconBg: string; accent: string; activeBg: string; badge?: string; badgeBg?: string }[] = [
  { icon: <Zap className="h-5 w-5" />, iconBg: "bg-primary/15 text-primary", accent: "text-primary", activeBg: "bg-gold-gradient" },
  { icon: <Sparkles className="h-5 w-5" />, iconBg: "bg-amber-500/15 text-amber-300", accent: "text-amber-400", activeBg: "bg-amber-500", badge: "Most popular", badgeBg: "bg-amber-500" },
];

/** Plan chrome for a tier, falling back to the Explorer look for a legacy plan. */
const tierMetaFor = (tierName: string | null | undefined) => TIER_META[tierColumnIndex(tierName, 0)] ?? TIER_META[0];

export function TierManagementCard() {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [currentTier, setCurrentTier] = useState<CurrentTier | null>(null);
  const [availableTiers, setAvailableTiers] = useState<AvailableTier[]>([]);
  const [loading, setLoading] = useState(true);
  const [showRenewModal, setShowRenewModal] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showUpgradeConfirm, setShowUpgradeConfirm] = useState<AvailableTier | null>(null);
  // Billing interval chosen in the upgrade/downgrade dialog. Seeded from the
  // buyer's live interval each time the dialog opens, so "keep what I have" is
  // the default and switching cycle is an explicit choice.
  const [confirmInterval, setConfirmInterval] = useState<"month" | "year">("month");
  const [showBenefitsModal, setShowBenefitsModal] = useState<AvailableTier | null>(null);
  const [renewInProgress, setRenewInProgress] = useState(false);
  const [cancelInProgress, setCancelInProgress] = useState(false);
  const [upgradeInProgress, setUpgradeInProgress] = useState(false);
  const [showRetryModal, setShowRetryModal] = useState(false);
  const [retryClientSecret, setRetryClientSecret] = useState<string | null>(null);
  const [retryAmount, setRetryAmount] = useState<number | null>(null);
  const [retryInProgress, setRetryInProgress] = useState(false);
  // Plan switch via checkout (non-Stripe-managed subscriptions only).
  const [switchTier, setSwitchTier] = useState<AvailableTier | null>(null);
  // ?upgrade=<tier name> — an upgrade CTA elsewhere in the app (BL Assistant,
  // any Operator-gated surface) deep-links here so the buyer lands ON the
  // dialog rather than being dropped on a settings page to find it themselves.
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkHandledRef = useRef(false);
  const [switchInProgress, setSwitchInProgress] = useState(false);
  // Optional cancellation reason, captured alongside the cancel request.
  const [feedbackReason, setFeedbackReason] = useState<string | null>(null);
  const [feedbackMessage, setFeedbackMessage] = useState("");
  // Cancellation feedback is mandatory: a reason must be picked, and "Other" needs a note.
  const feedbackComplete = feedbackReason !== null && (feedbackReason !== "Other" || feedbackMessage.trim().length > 0);
  const toast = useToast();

  const closeCancelModal = () => {
    setShowCancelConfirm(false);
    // Reset the feedback form so reopening the dialog starts clean.
    setFeedbackReason(null);
    setFeedbackMessage("");
  };

  // The subscription has lapsed. /current-tier may only just have found out (it
  // self-heals from Stripe), so the cached user still says has_active_subscription
  // and ChoosePlanRoute would bounce straight back to /buyer/home — the tab would
  // never render. Clear the stale flag first, then send them to pick a plan.
  const handleLapsed = () => {
    if (user?.has_active_subscription) updateUser({ ...user, has_active_subscription: false });
    navigate("/choose-plan");
  };

  useEffect(() => {
    Promise.all([
      api.get<CurrentTier>("/payments/current-tier"),
      api.get<AvailableTier[]>("/lookups/tiers"),
    ])
      .then(([tier, tiers]) => {
        // If subscription has already expired (cancels_at is in the past), go to choose-plan
        if (tier.cancels_at && new Date(tier.cancels_at) < new Date()) {
          handleLapsed();
          return;
        }
        setCurrentTier(tier);
        // Every other selectable plan is a change option — higher-priced rows
        // render as Upgrade, lower-priced as Downgrade (price, not id, decides:
        // grandfathered buyers can sit on retired tiers with out-of-order ids).
        setAvailableTiers((tiers as AvailableTier[]).filter(t => t.id !== tier.tier_id));
      })
      .catch((err) => {
        if (err instanceof ApiError && err.status === 404) {
          // No active subscription — redirect to plan selection
          handleLapsed();
        } else {
          toast.error("Unable to load subscription information");
        }
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toast, navigate]);

  // Fires once the tiers are in, since the target has to be matched by name.
  // The param is stripped afterwards so a refresh (or a back-navigation) does
  // not reopen a dialog the buyer already dismissed.
  useEffect(() => {
    if (deepLinkHandledRef.current || !currentTier) return;
    const wanted = searchParams.get("upgrade");
    if (!wanted) return;
    deepLinkHandledRef.current = true;
    const target = availableTiers.find(
      (t) => t.name.trim().toLowerCase() === wanted.trim().toLowerCase(),
    );
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("upgrade");
      return next;
    }, { replace: true });
    // Already on that plan (or it is gone) — the page still shows their
    // subscription, which is the right answer to "upgrade me to X".
    if (!target) return;
    // Read off currentTier directly: the `isAnnual` alias is declared further
    // down, past the loading early-return, so it isn't in scope up here.
    setConfirmInterval(currentTier.interval === "year" ? "year" : "month");
    // Same branch the Change Plan buttons take: Stripe-managed subs change in
    // place with proration, manual ones go through checkout.
    if (currentTier.stripe_managed) setShowUpgradeConfirm(target);
    else setSwitchTier(target);
  }, [currentTier, availableTiers, searchParams, setSearchParams]);

  // Renewal PaymentIntent is created ONLY when the buyer clicks Pay (deferred
  // mode in StripePaymentElement) — merely opening/closing the renewal modal
  // must not leave Incomplete PaymentIntents behind in Stripe.
  const createRenewIntent = async (): Promise<string> => {
    const res = await api.post<{ client_secret: string; payment_intent_id: string }>("/payments/create-tier-intent", {
      email: user?.email ?? "",
      tier_id: currentTier!.tier_id,
    });
    return res.client_secret;
  };

  // Fetch the open invoice's payment intent when the retry modal opens
  useEffect(() => {
    if (!showRetryModal) return;
    let cancelled = false;
    api.post<{ client_secret: string; payment_intent_id: string; amount: number }>("/payments/retry-payment", {})
      .then((res) => {
        if (cancelled) return;
        setRetryClientSecret(res.client_secret);
        setRetryAmount(res.amount);
      })
      .catch((err) => {
        if (cancelled) return;
        toast.error(err instanceof Error ? err.message : "Could not load payment. Please try again.");
        setShowRetryModal(false);
      });
    return () => {
      cancelled = true;
      setRetryClientSecret(null);
      setRetryAmount(null);
    };
  }, [showRetryModal]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRetrySuccess = async (paymentIntentId: string) => {
    setRetryInProgress(true);
    try {
      // Repoint future renewals at the card just used, so the next invoice isn't
      // retried against the old declined card and bounce the buyer back to past_due.
      // Non-fatal: the open invoice is already paid even if this call fails.
      try {
        await api.post("/payments/update-default-payment-method", { payment_intent_id: paymentIntentId });
      } catch { /* ignore — payment already succeeded */ }
      // The confirmed payment settles the open invoice; Stripe reactivates the subscription.
      toast.success("Payment successful — your subscription is active again.");
      setShowRetryModal(false);
      await reloadTier();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not refresh subscription.");
    } finally {
      setRetryInProgress(false);
    }
  };

  const reloadTier = async () => {
    const [updated, tiers] = await Promise.all([
      api.get<CurrentTier>("/payments/current-tier"),
      api.get<AvailableTier[]>("/lookups/tiers"),
    ]);
    setCurrentTier(updated);
    setAvailableTiers(tiers.filter(t => t.id !== updated.tier_id));
  };

  const handleCancel = async () => {
    // Feedback is mandatory — bail if the form isn't complete (the button is disabled too).
    if (!feedbackComplete) {
      toast.error("Please tell us why you're cancelling before continuing.");
      return;
    }
    setCancelInProgress(true);
    try {
      // Record the required cancellation reason first — non-fatal so a feedback
      // failure never blocks the actual cancellation.
      if (feedbackMessage.trim() || feedbackReason) {
        try {
          await api.post("/feedback", {
            message: feedbackMessage.trim() || feedbackReason,
            reason: feedbackReason,
            context: "cancellation",
          });
        } catch { /* ignore — don't block cancellation on feedback failure */ }
      }
      const res = await api.post<{ cancel_at_period_end: boolean; cancels_at: string | null }>(
        "/payments/cancel-subscription", {},
      );
      const endDate = res.cancels_at ? fmtFullDate(new Date(res.cancels_at)) : null;
      toast.success(
        endDate
          ? `Subscription will cancel on ${endDate}. You'll keep full access until then.`
          : "Subscription will cancel at the end of your billing period.",
      );
      closeCancelModal();
      await reloadTier();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to cancel subscription");
    } finally {
      setCancelInProgress(false);
    }
  };

  const handleUpgrade = async (tier: AvailableTier, interval: "month" | "year") => {
    const isUp = currentTier ? tier.amount_per_month > currentTier.amount_per_month : true;
    setUpgradeInProgress(true);
    try {
      await api.post("/payments/upgrade-subscription", { new_tier_id: tier.id, interval });
      toast.success(
        isUp
          ? `Upgraded to ${tier.name}! Proration charged to your saved card.`
          : `Downgraded to ${tier.name}. Your next renewal bills the lower price.`,
      );
      setShowUpgradeConfirm(null);
      await reloadTier();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Plan change failed");
    } finally {
      setUpgradeInProgress(false);
    }
  };

  // Non-Stripe (manual/promo) subscriptions have nothing to prorate — switching
  // plans means paying for the new tier through the normal checkout modal, which
  // creates a real Stripe subscription and re-activates the account on it.
  const handleSwitchSelected = async (selection: { tierId: number; subscriptionId?: string; promoCode?: string }) => {
    setSwitchInProgress(true);
    try {
      await api.post("/payments/activate-subscription", {
        tier_id: selection.tierId,
        stripe_subscription_id: selection.subscriptionId ?? null,
        promo_code: selection.promoCode ?? null,
      });
      // Subscription attached to the account — the redirect-resume stash is done.
      sessionStorage.removeItem(PENDING_STRIPE_PAYMENT_KEY);
      toast.success("Plan updated — your new subscription is active.");
      setSwitchTier(null);
      await reloadTier();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not switch plans. Please contact support.");
    } finally {
      setSwitchInProgress(false);
    }
  };

  // ── Resume a plan switch after a redirect-based Stripe payment ───────────
  // stripe.confirmPayment can leave this page entirely (bank redirects etc.);
  // TierSelector stashed the paid subscription in sessionStorage first, and
  // Stripe returns the browser here with ?redirect_status=…. Attach the
  // ALREADY-PAID subscription — reloading retries safely, and an activation
  // that never completes is cancelled + refunded by the daily sweep.
  const switchResumeRef = useRef(false);
  useEffect(() => {
    if (switchResumeRef.current) return;
    const redirectStatus = new URLSearchParams(window.location.search).get("redirect_status");
    if (!redirectStatus) return;
    const pendingPayment = readPendingStripePayment();
    if (!pendingPayment) return;
    switchResumeRef.current = true;

    if (redirectStatus !== "succeeded" && redirectStatus !== "processing") {
      sessionStorage.removeItem(PENDING_STRIPE_PAYMENT_KEY);
      toast.error("Payment was not completed. Please try again.");
      return;
    }
    void handleSwitchSelected({ tierId: pendingPayment.tierId, subscriptionId: pendingPayment.subscriptionId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRenewSuccess = async (paymentIntentId: string) => {
    setRenewInProgress(true);
    try {
      await api.post("/payments/renew-subscription", { payment_intent_id: paymentIntentId });
      toast.success("Subscription renewed successfully.");
      setShowRenewModal(false);
      await reloadTier();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Renewal failed");
    } finally {
      setRenewInProgress(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3 text-muted-foreground">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="text-sm">Loading your subscription…</span>
      </div>
    );
  }

  if (!currentTier) {
    return (
      <div className="rounded-2xl border border-dashed border-border bg-slate-50/40 p-10 text-center">
        <p className="text-muted-foreground text-sm">No active subscription found.</p>
      </div>
    );
  }

  const meta = tierMetaFor(currentTier.tier_name);
  // Short summary of the LIVE plan, straight from the tier row. The full
  // choose-plan benefit list belongs in the benefits / upgrade dialogs — this
  // card is a status card, not a pitch.
  const features = currentTier.features_text?.trim().split("\n").filter(Boolean) ?? [];
  const nextDate = currentTier.next_billing_date ? new Date(currentTier.next_billing_date) : null;
  const cancelsAtDate = currentTier.cancels_at ? new Date(currentTier.cancels_at) : null;
  const startDate = currentTier.started_at ? new Date(currentTier.started_at) : null;
  const trialEndDate = currentTier.trial_end ? new Date(currentTier.trial_end) : null;
  const fmtFullDate = (d: Date) => d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
  // Grandfathered on a retired tier: the plan is being phased out, so once it ends
  // the buyer picks a new plan (they cannot revert to the old pricing).
  const isLegacy = !currentTier.tier_is_selectable;
  // Billing interval of the live subscription.
  const isAnnual = currentTier.interval === "year";
  const cyclePrice = isAnnual && currentTier.amount_per_year ? currentTier.amount_per_year : currentTier.amount_per_month;
  const cycleSuffix = isAnnual ? "/year" : "/month";
  const cycleWord = isAnnual ? "annually" : "monthly";
  // Real next charge: prorated upcoming invoice when present, else the cycle price.
  const nextChargeAmount = currentTier.upcoming_charge_amount ?? cyclePrice;
  const cardLabel = currentTier.card_last4
    ? `${currentTier.card_brand ? currentTier.card_brand.charAt(0).toUpperCase() + currentTier.card_brand.slice(1) : "Card"} ···· ${currentTier.card_last4}`
    : "On file";

  // ── Promo code the buyer redeemed ──────────────────────────────────────
  // promo_is_current means this redemption is what's granting access right now
  // (manual billing, code matches the live tier). Anything else is history and
  // is shown as such so a Stripe-paying buyer isn't told a code covers them.
  const promoActive = Boolean(currentTier.promo_code) && currentTier.promo_is_current;
  const promoRedeemedDate = currentTier.promo_redeemed_at ? new Date(currentTier.promo_redeemed_at) : null;
  const promoExpiresDate = currentTier.promo_expires_at ? new Date(currentTier.promo_expires_at) : null;
  // Whole days left, counted the way access is gated: the expiry date itself still works.
  const promoDaysLeft = promoExpiresDate
    ? Math.ceil((promoExpiresDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    : null;
  const promoExpired = promoDaysLeft !== null && promoDaysLeft < 0;
  // A manual renewal pushes next_billing_date past the code's own window — say so
  // rather than claiming the promo itself runs that long.
  const promoExtended = Boolean(
    promoActive && promoRedeemedDate && promoExpiresDate && currentTier.promo_access_days != null &&
    promoExpiresDate.getTime() - promoRedeemedDate.getTime() > (currentTier.promo_access_days + 1) * 24 * 60 * 60 * 1000,
  );

  return (
    <>
      <div className="space-y-5">

        {/* ── Payment Failed Banner ── */}
        {currentTier.payment_failed && (
          <div className="rounded-2xl border-2 border-red-300 bg-red-50 px-6 py-5 shadow-sm">
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                <AlertTriangle className="h-5 w-5 text-red-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-red-800">Your last payment didn't go through</p>
                <p className="text-sm text-red-700 mt-1 leading-relaxed">
                  We couldn't charge your card for the <span className="font-semibold">{currentTier.tier_name}</span> plan.
                  Your access is paused until the payment goes through — retry to restore it right away.
                </p>
                <button
                  onClick={() => setShowRetryModal(true)}
                  className="mt-3 inline-flex items-center gap-2 rounded-xl bg-red-600 text-white text-sm font-bold px-4 py-2.5 shadow-sm hover:bg-red-700 transition-colors"
                >
                  <CreditCard className="h-4 w-4" />
                  Retry payment
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Current Plan Card ── */}
        <div className="rounded-2xl border border-border bg-white shadow-sm overflow-hidden">
          <div className="px-6 py-5 border-b border-border bg-slate-50/60 flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className={`h-11 w-11 rounded-xl flex items-center justify-center ${meta.iconBg}`}>
                {meta.icon}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <p className="text-lg font-extrabold text-foreground">{currentTier.tier_name}</p>
                  {meta.badge && (
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full text-white ${meta.badgeBg}`}>{meta.badge}</span>
                  )}
                </div>
                <p className={`text-sm font-semibold ${meta.accent}`}>${cyclePrice.toLocaleString()}{cycleSuffix}</p>
              </div>
            </div>
            {currentTier.payment_failed ? (
              <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold bg-red-50 text-red-700 border border-red-200 rounded-full px-3 py-1 shrink-0">
                <AlertTriangle className="h-3.5 w-3.5" />
                Payment due
              </span>
            ) : currentTier.is_trialing ? (
              <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold bg-primary/10 text-primary border border-primary/25 rounded-full px-3 py-1 shrink-0">
                <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
                Free trial
              </span>
            ) : currentTier.cancel_at_period_end ? (
              <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold bg-amber-50 text-amber-700 border border-amber-200 rounded-full px-3 py-1 shrink-0">
                <AlertTriangle className="h-3.5 w-3.5" />
                Cancels {cancelsAtDate?.toLocaleDateString("en-US", { month: "short", day: "numeric" })}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-3 py-1 shrink-0">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
                Active
              </span>
            )}
          </div>

          <div className="px-6 py-5 space-y-4">
            {features.length > 0 && (
              <ul className="space-y-2">
                {features.map((feat, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                    <Check className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                    <span>{feat}</span>
                  </li>
                ))}
              </ul>
            )}
            <div className="flex flex-wrap gap-3 pt-1">
              {startDate && (
                <div className="flex items-center gap-2 rounded-xl border border-border bg-slate-50 px-3.5 py-2 text-xs text-muted-foreground">
                  <CalendarDays className="h-3.5 w-3.5 text-slate-400" />
                  Started {startDate.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                </div>
              )}
              {currentTier.is_trialing && trialEndDate ? (
                <div className="flex items-center gap-2 rounded-xl border border-primary/25 bg-primary/10 px-3.5 py-2 text-xs text-primary font-medium">
                  <RefreshCw className="h-3.5 w-3.5 text-primary/70" />
                  First charge {trialEndDate.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                </div>
              ) : promoActive && promoExpiresDate ? (
                /* Promo access isn't a renewal — nothing bills on this date, access
                   simply runs out, so label it that way instead of "Renews". */
                <div className="flex items-center gap-2 rounded-xl border border-primary/25 bg-primary/10 px-3.5 py-2 text-xs text-primary font-medium">
                  <Clock className="h-3.5 w-3.5 text-primary/70" />
                  {promoExpired ? "Promo expired" : "Promo access until"}{" "}
                  {promoExpiresDate.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                </div>
              ) : nextDate && !currentTier.cancel_at_period_end && (
                <div className="flex items-center gap-2 rounded-xl border border-border bg-slate-50 px-3.5 py-2 text-xs text-muted-foreground">
                  <RefreshCw className="h-3.5 w-3.5 text-slate-400" />
                  Renews {nextDate.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                </div>
              )}
              {/* Only badge the plan itself when the code is the live grant — a
                  Stripe-paying buyer's old redemption belongs in the card below. */}
              {promoActive && (
                <div className="flex items-center gap-2 rounded-xl border border-primary/25 bg-primary/10 px-3.5 py-2 text-xs text-primary font-medium">
                  <Ticket className="h-3.5 w-3.5 text-primary/70" />
                  Promo <span className="font-mono font-bold tracking-wide">{currentTier.promo_code}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Promo Code Card ── shown whenever the buyer has ever redeemed a
            code: full details when it's the live grant, marked as history when
            they've since moved onto a paid Stripe subscription. */}
        {currentTier.promo_code && (
          <div className="rounded-2xl border border-border bg-white shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-border bg-slate-50/60 flex items-start justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-primary/15 text-primary flex items-center justify-center shrink-0">
                  <Ticket className="h-5 w-5" />
                </div>
                <div>
                  <p className="font-bold text-foreground">Promo code applied</p>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    {promoActive
                      ? `This code is what's giving you ${currentTier.tier_name} access — no card is charged for it.`
                      : "Redeemed on this account. Your current access comes from your paid subscription."}
                  </p>
                </div>
              </div>
              {promoActive ? (
                promoExpired ? (
                  <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold bg-red-500/10 text-red-500 border border-red-500/25 rounded-full px-3 py-1 shrink-0">
                    <AlertTriangle className="h-3.5 w-3.5" />
                    Expired
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold bg-emerald-500/10 text-emerald-500 border border-emerald-500/25 rounded-full px-3 py-1 shrink-0">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
                    Active
                  </span>
                )
              ) : (
                <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold bg-slate-100 text-muted-foreground border border-border rounded-full px-3 py-1 shrink-0">
                  Past redemption
                </span>
              )}
            </div>

            <div className="px-6 py-5 space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="rounded-xl border border-primary/25 bg-primary/10 px-3 py-3">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1">Code</p>
                  <p className="text-sm font-mono font-bold text-primary tracking-wide truncate" title={currentTier.promo_code}>
                    {currentTier.promo_code}
                  </p>
                </div>
                <div className="rounded-xl border border-border bg-slate-50 px-3 py-3">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1">Redeemed</p>
                  <p className="text-sm font-bold text-foreground">
                    {promoRedeemedDate
                      ? promoRedeemedDate.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
                      : "—"}
                  </p>
                </div>
                <div className="rounded-xl border border-border bg-slate-50 px-3 py-3">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1">Access granted</p>
                  <p className="text-sm font-bold text-foreground">
                    {currentTier.promo_access_days != null
                      ? `${currentTier.promo_access_days.toLocaleString()} day${currentTier.promo_access_days === 1 ? "" : "s"}`
                      : "—"}
                  </p>
                </div>
                <div className="rounded-xl border border-border bg-slate-50 px-3 py-3">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1">
                    {promoExpired ? "Ended" : "Valid until"}
                  </p>
                  <p className="text-sm font-bold text-foreground">
                    {promoExpiresDate
                      ? promoExpiresDate.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
                      : "—"}
                  </p>
                </div>
              </div>

              {/* Plain-language summary of what the code did and what happens next */}
              {promoActive && promoExpiresDate && (
                promoExpired ? (
                  <div className="flex items-start gap-3 rounded-xl bg-red-500/10 border border-red-500/25 px-4 py-3.5 text-sm text-red-500">
                    <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                    <p className="text-foreground/90">
                      Your promo access ended on <span className="font-semibold">{fmtFullDate(promoExpiresDate)}</span>.
                      Renew below or pick a plan to get your {currentTier.tier_name} features back.
                    </p>
                  </div>
                ) : promoDaysLeft !== null && promoDaysLeft <= 14 ? (
                  <div className="flex items-start gap-3 rounded-xl bg-amber-500/10 border border-amber-500/25 px-4 py-3.5 text-sm text-foreground/90">
                    <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
                    <p>
                      <span className="font-semibold">{promoDaysLeft} day{promoDaysLeft === 1 ? "" : "s"} left</span> on this code —
                      access ends <span className="font-semibold">{fmtFullDate(promoExpiresDate)}</span>. Renew below to keep your {currentTier.tier_name} plan without a gap.
                    </p>
                  </div>
                ) : (
                  <div className="flex items-start gap-3 rounded-xl bg-primary/10 border border-primary/25 px-4 py-3.5 text-sm text-foreground/90">
                    <CalendarCheck className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                    <p>
                      {currentTier.promo_access_days != null && promoRedeemedDate && (
                        <>Redeemed on <span className="font-semibold">{fmtFullDate(promoRedeemedDate)}</span> for{" "}
                          <span className="font-semibold">{currentTier.promo_access_days.toLocaleString()} days</span> of {currentTier.tier_name} access. </>
                      )}
                      {promoDaysLeft !== null && (
                        <><span className="font-semibold">{promoDaysLeft.toLocaleString()} day{promoDaysLeft === 1 ? "" : "s"} remaining</span> — access runs through{" "}</>
                      )}
                      <span className="font-semibold">{fmtFullDate(promoExpiresDate)}</span>, then it ends unless you renew.
                      {promoExtended && " (Includes the cycles you've paid for on top of the code.)"}
                    </p>
                  </div>
                )
              )}
            </div>
          </div>
        )}

        {/* ── Billing Section ── */}
        {currentTier.stripe_managed ? (
          <div className="rounded-2xl border border-border bg-white shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-border bg-slate-50/60 flex items-center justify-between">
              <div>
                <p className="font-bold text-foreground">Billing & Renewal</p>
                <p className="text-sm text-muted-foreground mt-0.5">Managed automatically by Stripe</p>
              </div>
              {currentTier.cancel_at_period_end ? (
                <span className="text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-3 py-1">
                  {isLegacy ? "Plan ending" : "Cancellation scheduled"}
                </span>
              ) : (
                <div className="flex items-center gap-1.5 text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1">
                  <ShieldCheck className="h-3.5 w-3.5" />
                  Auto-renewal on
                </div>
              )}
            </div>
            <div className="px-6 py-5 space-y-4">
              {/* Cancellation / plan-retirement warning */}
              {currentTier.cancel_at_period_end && cancelsAtDate && (
                <div className="flex items-start gap-3 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3.5 text-sm text-amber-800">
                  <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
                  <div className="flex-1">
                    {isLegacy ? (
                      // Grandfathered plan being retired: no "undo" — they move to the
                      // new plans (Explorer / Operator) once the paid period ends.
                      <p>
                        Your current plan is being retired and ends on{" "}
                        <span className="font-semibold">{cancelsAtDate.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</span>.
                        You'll keep full access until then — after that, choose one of our new plans (Explorer or Operator) to continue.
                      </p>
                    ) : (
                      <p>Your subscription will end on <span className="font-semibold">{cancelsAtDate.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</span>. You'll keep full access until then.</p>
                    )}
                  </div>
                </div>
              )}

              {/* Trial info */}
              {!currentTier.cancel_at_period_end && currentTier.is_trialing && (
                <div className="flex items-start gap-3 rounded-xl bg-primary/10 border border-primary/25 px-4 py-3.5 text-sm text-foreground/90">
                  <Info className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                  <p>
                    You're on a <span className="font-semibold">free trial</span>
                    {trialEndDate && <> until <span className="font-semibold">{fmtFullDate(trialEndDate)}</span></>}.
                    {" "}Your first charge of <span className="font-semibold">${nextChargeAmount.toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>
                    {trialEndDate && <> will be on <span className="font-semibold">{fmtFullDate(trialEndDate)}</span></>},
                    then <span className="font-semibold">${cyclePrice.toLocaleString()}{cycleSuffix}</span>.
                    Cancel before then and you won't be charged.
                  </p>
                </div>
              )}

              {/* Auto-renewal info */}
              {!currentTier.cancel_at_period_end && !currentTier.is_trialing && (
                <div className="flex items-start gap-3 rounded-xl bg-primary/10 border border-primary/25 px-4 py-3.5 text-sm text-foreground/90">
                  <Info className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                  <p>
                    Auto-renews {cycleWord}.{" "}
                    {nextDate && <>Next charge of <span className="font-semibold">${nextChargeAmount.toLocaleString()}</span> on <span className="font-semibold">{fmtFullDate(nextDate)}</span>.</>}
                  </p>
                </div>
              )}

              {/* Stats grid */}
              <div className="grid grid-cols-3 gap-3 text-center">
                {[
                  { label: "Amount", value: `$${cyclePrice.toLocaleString()}` },
                  { label: "Cycle", value: isAnnual ? "Annual" : "Monthly" },
                  { label: "Card", value: cardLabel },
                ].map(({ label, value }) => (
                  <div key={label} className="rounded-xl border border-border bg-slate-50 px-3 py-3">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1">{label}</p>
                    <p className="text-sm font-bold text-foreground truncate">{value}</p>
                  </div>
                ))}
              </div>

              {/* Cancel button */}
              {!currentTier.cancel_at_period_end && (
                <div className="pt-1 flex justify-end">
                  <button
                    onClick={() => setShowCancelConfirm(true)}
                    className="text-xs text-red-500 hover:text-red-700 font-medium underline hover:no-underline transition-colors"
                  >
                    Cancel subscription
                  </button>
                </div>
              )}
            </div>
          </div>
        ) : (
          /* Legacy manual renewal */
          <div className="rounded-2xl border border-border bg-white shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-border bg-slate-50/60">
              <p className="font-bold text-foreground">Monthly Renewal</p>
              <p className="text-sm text-muted-foreground mt-0.5">
                {promoActive && !promoExpired && promoExpiresDate
                  ? `Pay one cycle to add 30 days on top of your promo access (currently through ${fmtFullDate(promoExpiresDate)}).`
                  : "Pay one cycle to extend your subscription by 30 days."}
              </p>
            </div>
            <div className="px-6 py-5 space-y-4">
              <button
                onClick={() => setShowRenewModal(true)}
                className="w-full h-12 rounded-xl bg-gold-gradient text-primary-foreground text-sm font-bold flex items-center justify-center gap-2 shadow-md hover:brightness-[1.06] transition-[filter]"
              >
                <Lock className="h-4 w-4" />
                Pay ${currentTier.amount_per_month.toLocaleString()} Now
              </button>
              <p className="text-center text-[11px] text-muted-foreground">
                <ShieldCheck className="inline h-3 w-3 text-emerald-500 mr-1" />
                Secure payment · Powered by Stripe
              </p>
            </div>
          </div>
        )}

        {/* ── Change Plan Section (upgrade + downgrade) ── */}
        {!currentTier.cancel_at_period_end && availableTiers.length > 0 && (
          <div className="rounded-2xl border border-border bg-white shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-border bg-slate-50/60">
              <p className="font-bold text-foreground">Change Plan</p>
              <p className="text-sm text-muted-foreground mt-0.5">
                Upgrades charge the prorated difference immediately. Downgrades apply now — your next renewal bills the lower price.
              </p>
            </div>
            <div className="px-6 py-5 space-y-3">
              {availableTiers.map((tier) => {
                const tierMeta = tierMetaFor(tier.name);
                const diff = tier.amount_per_month - currentTier.amount_per_month;
                const isUp = diff > 0;
                return (
                  <div key={tier.id} className="flex items-center justify-between rounded-xl border border-border bg-slate-50/60 px-4 py-3.5 gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`h-9 w-9 rounded-xl flex items-center justify-center shrink-0 ${tierMeta.iconBg}`}>
                        {tierMeta.icon}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                          <p className="text-sm font-bold text-foreground">{tier.name}</p>
                          {tierMeta.badge && (
                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full text-white ${tierMeta.badgeBg}`}>{tierMeta.badge}</span>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          ${tier.amount_per_month.toLocaleString()}/mo · {isUp ? `+$${diff.toLocaleString()}/mo more` : `$${Math.abs(diff).toLocaleString()}/mo less`}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        onClick={() => setShowBenefitsModal(tier)}
                        className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-2 rounded-lg border border-border bg-white text-foreground hover:bg-slate-50 transition-colors"
                        title="View benefits"
                      >
                        <Info className="h-3.5 w-3.5 text-muted-foreground" />
                        Benefits
                      </button>
                      {/* Stripe-managed subs change in place (prorated / next-cycle);
                          manual subs go through checkout for the new tier. */}
                      <button
                        onClick={() => {
                          setConfirmInterval(isAnnual ? "year" : "month");
                          currentTier.stripe_managed ? setShowUpgradeConfirm(tier) : setSwitchTier(tier);
                        }}
                        className={
                          isUp
                            ? `inline-flex items-center gap-1.5 text-xs font-bold px-3.5 py-2 rounded-lg text-primary-foreground shadow-sm transition-colors ${tierMeta.activeBg} hover:opacity-90`
                            : "inline-flex items-center gap-1.5 text-xs font-bold px-3.5 py-2 rounded-lg border border-border bg-white text-foreground hover:bg-slate-50 transition-colors"
                        }
                      >
                        {isUp ? <ArrowUpCircle className="h-3.5 w-3.5" /> : <ArrowDownCircle className="h-3.5 w-3.5" />}
                        {isUp ? "Upgrade" : "Downgrade"}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* ── Tier Benefits Modal ── */}
      {showBenefitsModal && (() => {
        const bMeta = tierMetaFor(showBenefitsModal.name);
        const bFeatures = showBenefitsModal.features_text?.trim().split("\n").filter(Boolean) ?? [];
        const diff = showBenefitsModal.amount_per_month - (currentTier?.amount_per_month ?? 0);
        // The plan's own bullet list from the choose-plan page, so "Benefits"
        // and "Choose my plan" describe the plan identically. A legacy tier has
        // no column there, so it falls back to its own features_text.
        const bRows = planFeatures(showBenefitsModal.name);
        const bColumn = tierColumnIndex(showBenefitsModal.name);

        return (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ backgroundColor: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)" }}
            onClick={(e) => { if (e.target === e.currentTarget) setShowBenefitsModal(null); }}
          >
            <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
              {/* Header */}
              <div className={`px-6 pt-5 pb-5 border-b border-border`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className={`h-11 w-11 rounded-xl flex items-center justify-center ${bMeta.iconBg}`}>
                      {bMeta.icon}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="text-lg font-extrabold text-foreground">{showBenefitsModal.name}</p>
                        {bMeta.badge && (
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full text-white ${bMeta.badgeBg}`}>{bMeta.badge}</span>
                        )}
                      </div>
                      <p className={`text-sm font-bold ${bMeta.accent}`}>
                        ${showBenefitsModal.amount_per_month.toLocaleString()}/month
                        <span className="ml-2 text-xs text-muted-foreground font-normal">
                          ({diff >= 0 ? "+" : "−"}${Math.abs(diff).toLocaleString()}/mo vs current plan)
                        </span>
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowBenefitsModal(null)}
                    className="h-8 w-8 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:bg-slate-100 shrink-0"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>

                {bColumn >= 0 && (
                  <div className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-semibold bg-slate-100 text-slate-600 rounded-full px-2.5 py-1">
                    TIER {bColumn + 1}
                  </div>
                )}
              </div>

              <div className="px-6 py-5 space-y-4 max-h-[60dvh] overflow-y-auto">
                {/* Full feature list — the choose-plan card's bullets */}
                {(bRows.length > 0 ? bRows : bFeatures).length > 0 && (
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-2.5">What's included</p>
                    <ul className="space-y-2">
                      {(bRows.length > 0 ? bRows : bFeatures).map((feat, i) => (
                        <li key={i} className="flex items-start gap-2.5 text-sm text-muted-foreground">
                          <span className={`mt-0.5 h-4 w-4 rounded-full flex items-center justify-center shrink-0 ${bMeta.activeBg}`}>
                            <Check className="h-2.5 w-2.5 text-white" />
                          </span>
                          <span>{feat}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Footer actions */}
              <div className="flex gap-3 px-6 pb-6 pt-2">
                <button
                  onClick={() => setShowBenefitsModal(null)}
                  className="flex-1 h-11 rounded-xl border border-border text-sm font-semibold text-foreground hover:bg-slate-50 transition-colors"
                >
                  Not now
                </button>
                <button
                  onClick={() => {
                    const target = showBenefitsModal;
                    setShowBenefitsModal(null);
                    setConfirmInterval(isAnnual ? "year" : "month");
                    if (currentTier?.stripe_managed) setShowUpgradeConfirm(target);
                    else setSwitchTier(target);
                  }}
                  className={`flex-1 h-11 rounded-xl text-primary-foreground text-sm font-bold transition-colors flex items-center justify-center gap-2 ${bMeta.activeBg} hover:opacity-90`}
                >
                  {diff >= 0 ? <ArrowUpCircle className="h-4 w-4" /> : <ArrowDownCircle className="h-4 w-4" />}
                  {diff >= 0 ? "Upgrade" : "Downgrade"} to {showBenefitsModal.name}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Cancel Subscription Confirmation Dialog ── */}
      {showCancelConfirm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)" }}
          onClick={(e) => { if (e.target === e.currentTarget && !cancelInProgress) closeCancelModal(); }}
        >
          <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden max-h-[92dvh] flex flex-col">

            {/* Header */}
            <div className="px-6 pt-5 pb-4 border-b border-border shrink-0">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-red-50 flex items-center justify-center shrink-0">
                    <AlertTriangle className="h-5 w-5 text-red-500" />
                  </div>
                  <div>
                    <p className="font-extrabold text-foreground text-base">Cancel subscription</p>
                    <p className="text-xs text-muted-foreground mt-0.5">We're sorry to see you go</p>
                  </div>
                </div>
                <button
                  onClick={closeCancelModal}
                  disabled={cancelInProgress}
                  className="h-8 w-8 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:bg-slate-100 shrink-0 mt-0.5 disabled:opacity-50"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="overflow-y-auto">
              <div className="px-6 pt-4 pb-3">
                <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
                  <p className="text-sm text-amber-900 leading-relaxed">
                    {nextDate ? (
                      <>Your subscription will stay active until{" "}
                        <span className="font-semibold">{fmtFullDate(nextDate)}</span>, then cancel — you won't be charged again. You keep full access until then.</>
                    ) : (
                      <>Your subscription will cancel at the end of your current billing period. You keep full access until then and won't be charged again.</>
                    )}
                  </p>
                </div>
              </div>

              {/* ── Required reason ── */}
              <div className="px-6 pb-2">
                <div className="rounded-xl border border-border bg-slate-50/60 px-4 py-3.5">
                  <div className="flex items-center gap-2 mb-1">
                    <MessageSquare className="h-4 w-4 text-primary" />
                    <p className="text-sm font-bold text-foreground">Mind sharing why? <span className="font-normal text-xs text-red-500">(required)</span></p>
                  </div>

                  {/* Reason chips */}
                  <div className="flex flex-wrap gap-1.5 mt-2.5 mb-2.5">
                    {CANCEL_REASONS.map((reason) => {
                      const active = feedbackReason === reason;
                      return (
                        <button
                          key={reason}
                          type="button"
                          onClick={() => setFeedbackReason(active ? null : reason)}
                          className={`text-xs font-medium px-2.5 py-1.5 rounded-full border transition-colors ${
                            active
                              ? "bg-gold-gradient text-primary-foreground border-primary"
                              : "bg-white text-muted-foreground border-border hover:border-primary/40 hover:text-foreground"
                          }`}
                        >
                          {reason}
                        </button>
                      );
                    })}
                  </div>

                  <textarea
                    value={feedbackMessage}
                    onChange={(e) => setFeedbackMessage(e.target.value)}
                    placeholder={feedbackReason === "Other" ? "Please tell us more… (required)" : "Tell us more…"}
                    rows={3}
                    maxLength={2000}
                    className="w-full resize-none rounded-lg border border-border bg-white px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />

                  {!feedbackComplete && (
                    <p className="text-xs text-muted-foreground mt-2">
                      {feedbackReason === "Other"
                        ? "Add a quick note above to continue."
                        : "Select a reason to continue."}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Footer — actions stay pinned and fully visible on any screen height */}
            <div className="px-6 py-4 shrink-0 border-t border-border">
              <div className="flex gap-3">
                <button
                  onClick={closeCancelModal}
                  disabled={cancelInProgress}
                  className="flex-1 h-11 rounded-xl border border-border text-sm font-semibold text-foreground hover:bg-slate-50 transition-colors disabled:opacity-50"
                >
                  Keep subscription
                </button>
                <button
                  onClick={handleCancel}
                  disabled={cancelInProgress || !feedbackComplete}
                  title={!feedbackComplete ? "Please tell us why you're cancelling first" : undefined}
                  className="flex-1 h-11 rounded-xl bg-red-600 text-white text-sm font-bold flex items-center justify-center gap-2 hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {cancelInProgress ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  Cancel subscription
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Plan Change Confirm Dialog (upgrade or downgrade) — large two-column
          layout: the target plan's full benefits on the left (same content as
          the choose-plan cards), payment breakdown + CTA on the right. ── */}
      {showUpgradeConfirm && currentTier && (() => {
        const upgradeMeta = tierMetaFor(showUpgradeConfirm.name);
        // Direction is always judged on the monthly price — that's the plan
        // ordering. Switching cycle doesn't turn a downgrade into an upgrade.
        const priceDiff = showUpgradeConfirm.amount_per_month - currentTier.amount_per_month;
        const isUp = priceDiff > 0;
        const yearlyAvailable = showUpgradeConfirm.amount_per_year != null;
        const wantsYearly = confirmInterval === "year" && yearlyAvailable;
        // Switching billing cycle changes what Stripe charges today (it credits
        // the unused part of the current cycle), so we can't state a figure —
        // only a same-cycle change has a proration we can compute here.
        const cycleChanged = wantsYearly !== isAnnual;
        const newCyclePrice = wantsYearly
          ? (showUpgradeConfirm.amount_per_year as number)
          : showUpgradeConfirm.amount_per_month;
        const newCycleSuffix = wantsYearly ? "/yr" : "/mo";
        const currentCyclePrice = isAnnual && currentTier.amount_per_year
          ? currentTier.amount_per_year
          : currentTier.amount_per_month;
        const currentCycleSuffix = isAnnual ? "/yr" : "/mo";
        // Yearly saving vs paying the same plan monthly for 12 months.
        const yearlySavePct = showUpgradeConfirm.amount_per_year != null && showUpgradeConfirm.amount_per_month > 0
          ? Math.round((1 - showUpgradeConfirm.amount_per_year / (showUpgradeConfirm.amount_per_month * 12)) * 100)
          : 0;
        const daysRemaining = nextDate ? Math.max(1, Math.ceil((nextDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24))) : 30;
        const daysInCycle = 30;
        const proratedAmount = Math.round((priceDiff / daysInCycle) * daysRemaining);
        const nextRenewalAmount = newCyclePrice;
        const confirmFeatures = showUpgradeConfirm.features_text?.trim().split("\n").filter(Boolean) ?? [];
        // What the buyer is buying, worded exactly as the choose-plan page words
        // it. A legacy tier has no column there, so it falls back to its own
        // features_text.
        const confirmRows = planFeatures(showUpgradeConfirm.name);
        return (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6"
          style={{ backgroundColor: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)" }}
          onClick={(e) => { if (e.target === e.currentTarget && !upgradeInProgress) setShowUpgradeConfirm(null); }}
        >
          <div
            className="relative flex w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl animate-in fade-in zoom-in-95 duration-200"
            style={{ maxHeight: "calc(100vh / var(--app-zoom) - 3rem)" }}
          >
            {/* Header */}
            <div className="flex items-start justify-between gap-3 px-6 py-5 border-b border-border shrink-0">
              <div className="flex items-center gap-3.5">
                <div className={`h-12 w-12 rounded-xl flex items-center justify-center ${upgradeMeta.iconBg}`}>
                  {upgradeMeta.icon}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-extrabold text-foreground text-xl">{isUp ? "Upgrade" : "Downgrade"} to {showUpgradeConfirm.name}</p>
                    {upgradeMeta.badge && (
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full text-white ${upgradeMeta.badgeBg}`}>{upgradeMeta.badge}</span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    ${newCyclePrice.toLocaleString()}/{wantsYearly ? "year" : "month"} going forward
                  </p>
                </div>
              </div>
              {!upgradeInProgress && (
                <button
                  onClick={() => setShowUpgradeConfirm(null)}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-border hover:bg-slate-100 text-muted-foreground transition-colors shrink-0"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto">
              <div className="flex flex-col md:flex-row">

                {/* ── Left: what you get ── */}
                <div className="md:w-[46%] shrink-0 bg-slate-50/70 border-b md:border-b-0 md:border-r border-border px-6 py-5">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-3">
                    What {showUpgradeConfirm.name} includes
                  </p>

                  {(confirmRows.length > 0 ? confirmRows : confirmFeatures).length > 0 && (
                    <ul className="space-y-2.5">
                      {(confirmRows.length > 0 ? confirmRows : confirmFeatures).map((feat, i) => (
                        <li key={i} className="flex items-start gap-2.5 text-sm text-foreground/85">
                          <Check className={`h-4 w-4 shrink-0 mt-0.5 ${upgradeMeta.accent}`} />
                          <span>{feat}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* ── Right: payment breakdown ── */}
                <div className="flex-1 px-6 py-5 space-y-4">

                  {/* Billing cycle — only offered when the tier has an annual price */}
                  {yearlyAvailable && (
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-2">Billing cycle</p>
                      <div className="grid grid-cols-2 gap-2">
                        {([
                          { key: "month" as const, label: "Monthly", price: showUpgradeConfirm.amount_per_month, suffix: "/mo" },
                          { key: "year" as const, label: "Yearly", price: showUpgradeConfirm.amount_per_year as number, suffix: "/yr" },
                        ]).map((opt) => {
                          const active = confirmInterval === opt.key;
                          return (
                            <button
                              key={opt.key}
                              type="button"
                              onClick={() => setConfirmInterval(opt.key)}
                              disabled={upgradeInProgress}
                              className={`relative rounded-xl border px-3 py-2.5 text-left transition-colors disabled:opacity-60 ${
                                active ? "border-primary bg-primary/10" : "border-border bg-white hover:border-primary/40"
                              }`}
                            >
                              <span className="block text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                                {opt.label}
                              </span>
                              <span className="block text-sm font-extrabold text-foreground">
                                ${opt.price.toLocaleString()}
                                <span className="font-semibold text-muted-foreground">{opt.suffix}</span>
                              </span>
                              {opt.key === "year" && yearlySavePct > 0 && (
                                <span className="absolute right-2 top-2 rounded-full bg-emerald-500/15 px-1.5 py-0.5 text-[9px] font-bold text-emerald-600">
                                  SAVE {yearlySavePct}%
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <div className="rounded-xl border border-border overflow-hidden">
                    <div className="bg-slate-50/60 px-4 py-2.5 border-b border-border">
                      <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Payment breakdown</p>
                    </div>
                    <div className="divide-y divide-border">
                      <div className="flex justify-between items-center px-4 py-3.5 text-sm">
                        <span className="text-muted-foreground">Current plan</span>
                        <span className="font-medium line-through text-muted-foreground">${currentCyclePrice.toLocaleString()}{currentCycleSuffix}</span>
                      </div>
                      <div className="flex justify-between items-center px-4 py-3.5 text-sm">
                        <span className="text-muted-foreground">New plan</span>
                        <span className="font-bold text-foreground">${newCyclePrice.toLocaleString()}{newCycleSuffix}</span>
                      </div>
                      {cycleChanged ? (
                        <div className="flex justify-between items-center px-4 py-3.5 text-sm">
                          <div>
                            <span className="text-muted-foreground">Charged today</span>
                            <p className="text-[11px] text-muted-foreground/70 mt-0.5">
                              Switching to {wantsYearly ? "yearly" : "monthly"} billing — Stripe credits the unused part of your current cycle
                            </p>
                          </div>
                          <span className="font-bold text-foreground">Calculated at checkout</span>
                        </div>
                      ) : isUp ? (
                        <div className="flex justify-between items-center px-4 py-3.5 text-sm">
                          <div>
                            <span className="text-muted-foreground">Prorated charge today</span>
                            <p className="text-[11px] text-muted-foreground/70 mt-0.5">{daysRemaining} days left in cycle × ${(priceDiff / daysInCycle).toFixed(2)}/day</p>
                          </div>
                          <span className="font-extrabold text-foreground text-xl">${proratedAmount.toLocaleString()}</span>
                        </div>
                      ) : (
                        <div className="flex justify-between items-center px-4 py-3.5 text-sm">
                          <div>
                            <span className="text-muted-foreground">Charged today</span>
                            <p className="text-[11px] text-muted-foreground/70 mt-0.5">Current cycle stays as paid — no refund</p>
                          </div>
                          <span className="font-extrabold text-foreground text-xl">$0</span>
                        </div>
                      )}
                      <div className="flex justify-between items-center px-4 py-3.5 text-sm bg-slate-50/40">
                        <span className="text-muted-foreground">Next renewal ({nextDate?.toLocaleDateString("en-US", { month: "short", day: "numeric" })})</span>
                        <span className="font-semibold text-foreground">${nextRenewalAmount.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>

                  {/* Card info — only an upgrade charges the saved card today */}
                  {isUp && (
                    <div className="flex items-center gap-2.5 rounded-xl bg-slate-50 border border-border px-4 py-3.5 text-sm">
                      <CreditCard className="h-4 w-4 text-muted-foreground shrink-0" />
                      <span className="text-muted-foreground">Charged to</span>
                      <span className="font-semibold text-foreground">{cardLabel}</span>
                    </div>
                  )}

                  <div className="flex items-start gap-2.5 rounded-xl bg-primary/10 border border-primary/25 px-4 py-3.5 text-sm text-foreground/90">
                    <Info className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                    <p className="text-[13px] leading-relaxed">
                      {cycleChanged
                        ? `Your plan switches to ${showUpgradeConfirm.name} on ${wantsYearly ? "yearly" : "monthly"} billing. Stripe works out the exact amount from the time left on your current cycle.`
                        : isUp
                        ? "Access upgrades immediately after payment. The prorated amount may vary slightly."
                        : `Your plan switches to ${showUpgradeConfirm.name} immediately and features adjust right away.`}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer actions */}
            <div className="flex gap-3 px-6 py-4 border-t border-border bg-slate-50/40 shrink-0">
              <button
                onClick={() => setShowUpgradeConfirm(null)}
                disabled={upgradeInProgress}
                className="flex-1 h-12 rounded-xl border border-border text-sm font-semibold text-foreground hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleUpgrade(showUpgradeConfirm, wantsYearly ? "year" : "month")}
                disabled={upgradeInProgress}
                className={`flex-[1.5] h-12 rounded-xl text-base font-extrabold transition-colors flex items-center justify-center gap-2 disabled:opacity-60 shadow-md ${
                  isUp ? `text-primary-foreground ${upgradeMeta.activeBg}` : "border border-border bg-slate-100 text-foreground hover:bg-slate-200"
                }`}
              >
                {upgradeInProgress ? <Loader2 className="h-5 w-5 animate-spin" /> : isUp ? <ArrowUpCircle className="h-5 w-5" /> : <ArrowDownCircle className="h-5 w-5" />}
                {cycleChanged
                  ? `Confirm ${isUp ? "upgrade" : "downgrade"} · ${wantsYearly ? "Yearly" : "Monthly"}`
                  : isUp
                  ? `Pay $${proratedAmount.toLocaleString()} & Upgrade`
                  : "Confirm downgrade"}
              </button>
            </div>
          </div>
        </div>
        );
      })()}

      {/* ── Renewal Modal — same chrome as the choose-plan payment modal ── */}
      {showRenewModal && (
        <BrandedPaymentModal
          onClose={() => setShowRenewModal(false)}
          closable={!renewInProgress}
          banner={
            <>
              <span className="font-semibold">Charged today:</span>{" "}
              <span className="font-semibold">${currentTier.amount_per_month.toLocaleString()}</span> — extends your{" "}
              <span className="font-semibold">{currentTier.tier_name}</span> access another 30 days. One cycle, no auto-renewal.
            </>
          }
          planName={currentTier.tier_name}
          price={`$${currentTier.amount_per_month.toLocaleString()}`}
          priceNote="per 30 days"
          badges={["One-cycle renewal", "Cancel anytime"]}
          rows={[
            ["Plan", currentTier.tier_name],
            ["Billing", "One cycle · 30 days"],
            ["Due today", `$${currentTier.amount_per_month.toLocaleString()}`],
          ]}
        >
          <StripePaymentElement
            amount={currentTier.amount_per_month}
            createIntent={createRenewIntent}
            buttonLabel={`Pay $${currentTier.amount_per_month.toLocaleString()} — 30 days`}
            onSuccess={handleRenewSuccess}
            disabled={renewInProgress}
          />
        </BrandedPaymentModal>
      )}

      {/* ── Retry Payment Modal — same chrome as the choose-plan payment modal ── */}
      {showRetryModal && (
        <BrandedPaymentModal
          onClose={() => setShowRetryModal(false)}
          closable={!retryInProgress}
          banner={
            <>
              <span className="font-semibold">Amount due:</span>{" "}
              <span className="font-semibold">${(retryAmount ?? currentTier.amount_per_month).toLocaleString()}</span> — pay now to keep your{" "}
              <span className="font-semibold">{currentTier.tier_name}</span> plan active.
            </>
          }
          planName={currentTier.tier_name}
          price={`$${(retryAmount ?? currentTier.amount_per_month).toLocaleString()}`}
          priceNote="amount due"
          rows={[
            ["Plan", currentTier.tier_name],
            ["Status", "Payment declined"],
            ["Due today", `$${(retryAmount ?? currentTier.amount_per_month).toLocaleString()}`],
          ]}
          alert={
            <div className="flex items-start gap-3 rounded-xl bg-red-50 border border-red-200 px-4 py-3.5 text-sm text-red-800">
              <AlertTriangle className="h-4 w-4 text-red-500 shrink-0 mt-0.5" />
              <p>Your previous payment was declined. Update your card and try again to keep your <span className="font-semibold">{currentTier.tier_name}</span> plan active.</p>
            </div>
          }
        >
          {retryClientSecret ? (
            <StripePaymentElement
              clientSecret={retryClientSecret}
              buttonLabel={`Pay $${(retryAmount ?? currentTier.amount_per_month).toLocaleString()} Now`}
              onSuccess={handleRetrySuccess}
              disabled={retryInProgress}
            />
          ) : (
            <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground justify-center">
              <Loader2 className="h-4 w-4 animate-spin" />Preparing payment…
            </div>
          )}
        </BrandedPaymentModal>
      )}

      {/* ── Plan switch via checkout — manual (non-Stripe) subscriptions only.
          Opens the same payment modal as choose-plan for the target tier; on
          success the account is re-activated on the new Stripe subscription. ── */}
      {switchTier && (
        <TierSelector
          key={switchTier.id}
          registrationEmail={user?.email || ""}
          onTierSelected={handleSwitchSelected}
          isLoading={switchInProgress}
          directTierId={switchTier.id}
          directInterval="month"
          onClose={() => setSwitchTier(null)}
        />
      )}
    </>
  );
}
